/*
 * Model for knowledgeBases
 */

define([
  "backbone",
  "jquery",
  "underscore",
  "moment-timezone",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
], function (Backbone, $, _, Moment, Messenger, Properties, SortableListModel) {
  "use strict";

  const KbModel = {};

  const ENGINE_MODE = {
    NLP: 0,
    KEYWORD: 1,
    SEMANTIC: 2,
  };

  const TRANSLATION_MODE = {
    EMPLIFI_MACHINE_TRANSLATION: "EMPLIFI_MACHINE_TRANSLATION",
  };

  KbModel.ENGINE_MODE = ENGINE_MODE;

  KbModel.knowledgeBase = Backbone.Model.extend({
    idAttribute: "kbId",
    defaults: {
      kbName: "",
      kbDescription: "",
      creationDate: Moment(),
      tenantId: 0,
      dbConfigId: 1,
      tier: 3,
      responseCollaboration: true,
      responseVersioning: true,
      responseReview: false,
      exampleGeneration: false,
      agentAssist: false,
      conversations: true,
      taskManagement: false,
      actionGroups: false,
      adHocReporting: false,
      recommendations: false,
      crossTouchPointAccess: false,
      editSource: false,
      aiFeatures: false,
      customLibrarian: false,
      convertBackslashes: false,
      nlp: true,
      secureUploadAccess: true,
      siteMapAccess: false,
      pcasURL: "",
      company: "",
      pcasLogonUrl: "",
      pcasUsername: "",
      pcasPassword: "",
      pcasUserAgent: "",
      pcasVersion: "10",
      engineMode: ENGINE_MODE.NLP,
      translationMode: TRANSLATION_MODE.EMPLIFI_MACHINE_TRANSLATION,
    },
    defaultValidateAttributes: function () {
      if (this.get("agentAssist")) {
        return [
          "kbName",
          "kbDescription",
          "pcasURL",
          "company",
          "pcasUsername",
          "pcasPassword",
          "pcasLogonUrl",
          "pcasUserAgent",
        ];
      } else {
        return ["kbName", "kbDescription"];
      }
    },
    backup: function () {
      this.onSynced(function () {
        this.wasDirty = this.dirty;
        this.cloneCopy = this.clone();
      }, this);
    },
    restore: function () {
      this.set(this.cloneCopy.attributes);
      this.dirty = this.wasDirty;
    },
    getContexts: function () {
      this.contexts = this.getSubModel(this.contexts, KbModel.contexts, "/contexts");
      return this.contexts;
    },
    getTouchPoints: function () {
      this.touchpoints = this.getSubModel(this.touchpoints, KbModel.touchpoints, "/touchpoints");
      return this.touchpoints;
    },
    getLocales: function () {
      this.locales = this.getSubModel(this.locales, KbModel.languages, "/languages");
      return this.locales;
    },
    saveModel: function (options) {
      const newKb = this.isNew();
      if (this._allowSave(options)) {
        //call default save function with options
        const saveOptions = this._prepareSaveModelOptions(options);
        saveOptions.success = (model, response) => {
          model.dirty = false;
          if (newKb) {
            model.saveChildren(options);
          } else if (options && options.success) {
            // do not use javascript arguments object as it is not supported inside arrow function expression.
            // if you use arguments here, it will be a reference to the arguments of the enclosing scope. In this
            // case if would be 'options'
            options.success(model, response || {}, options);
          } else {
            Messenger.saveAttempted(true);
          }
        };
        this.save(null, saveOptions);
      } else if (options.success) {
        //fire success callback because nothing needed to be saved
        options.success(this, {}, options);
      } else {
        Messenger.saveAttempted(true);
      }
    },
    saveChildren: function (options) {
      //pass all the data needed to the child models for saving
      if (this.contexts) {
        this.contexts.kbId = this.get("kbId");
      }
      if (this.touchpoints) {
        this.touchpoints.kbId = this.get("kbId");
      }
      if (this.locales) {
        this.locales.kbId = this.get("kbId");
        //when adding a new KB, set the dirty flag on the locales in order to get saved as well
        this.locales.each(function (model) {
          model.dirty = true;
        }, this);
      }
      //override the type so the locales don't try to save as a PUT
      options.type = "POST";
      this.saveSubModels(options);
    },
    _validateDuplicateKbName: function (value) {
      if (this.collection) {
        let duplicateName = false;
        this.collection.each(function (kb) {
          //check if the other object has the same name (ignore case) and is actually a different object
          if (kb.get("kbName").toLowerCase() === value.toLowerCase() && kb.cid !== this.cid) {
            duplicateName = true;
          }
        }, this);
        if (duplicateName) {
          return $.t("admin.kbAdmin.duplicateNameError");
        }
      }
    },
    validateAttribute: function (attribute, value, allAttributes) {
      if (attribute === "kbName") {
        return (
          this.validateRequiredValue(value, { maxLength: 50 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateKbName(value)
        );
      }
      if (attribute === "kbDescription") {
        return this.validateStringLength(value, { maxLength: 255 });
      }
      if (attribute === "pcasURL") {
        const agentAssist = allAttributes ? allAttributes.agentAssist : this.get("agentAssist");
        if (agentAssist) {
          return this.validateRequiredValue(value, { maxLength: 500 });
        }
      }
      if (attribute === "pcasLogonUrl") {
        const agentAssist = allAttributes ? allAttributes.agentAssist : this.get("agentAssist");
        if (agentAssist) {
          const pcasVersion = allAttributes ? allAttributes.pcasVersion : this.get("pcasVersion");
          if (_.isEmpty(value) && pcasVersion == "11") {
            return $.t("commonMessages.emptyMessage");
          }
          return this.validateStringLength(value, { maxLength: 500 });
        }
      }
      if (attribute === "company") {
        const agentAssist = allAttributes ? allAttributes.agentAssist : this.get("agentAssist");
        if (agentAssist) {
          return (
            this.validateRequiredValue(value, { maxLength: 50 }) ||
            this.validateSafeCharacters(value)
          );
        }
      }
      if (attribute === "pcasUsername") {
        const agentAssist = allAttributes ? allAttributes.agentAssist : this.get("agentAssist");
        if (agentAssist) {
          return (
            this.validateRequiredValue(value, { maxLength: 50 }) ||
            this.validateSafeCharacters(value)
          );
        }
      }
      if (attribute === "pcasPassword") {
        const agentAssist = allAttributes ? allAttributes.agentAssist : this.get("agentAssist");
        if (agentAssist) {
          return this.validateRequiredValue(value, { maxLength: 50 });
        }
      }
      if (attribute === "pcasUserAgent") {
        const agentAssist = allAttributes ? allAttributes.agentAssist : this.get("agentAssist");
        if (agentAssist) {
          return this.validateSafeCharacters(value);
        }
      }
    },
    //define a URL Root for when requesting the model outside of collection
    urlRoot: function () {
      return Properties.adminRootURL + "/kbs";
    },
  });

  KbModel.knowledgeBases = Backbone.Collection.extend({
    model: KbModel.knowledgeBase,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "kbName");
    },
    addNewKb: function () {
      const newKb = new KbModel.knowledgeBase();
      this.add(newKb);
      return newKb;
    },
    url: function () {
      return Properties.adminRootURL + "/kbs";
    },
  });

  KbModel.context = Backbone.Model.extend({
    idAttribute: "id",
    defaultValidateAttributes: ["status"],
    _validateDuplicateStatus: function (value) {
      for (let i = 0; i < this.collection.length; i++) {
        const context = this.collection.at(i);
        if (this !== context) {
          //don't check the entry against itself
          if (value.toUpperCase() === context.get("status").toUpperCase()) {
            return $.t("admin.kbAdmin.duplicateContextError");
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "status") {
        return (
          this.validateRequiredValue(value, { maxLength: 50 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateStatus(value)
        );
      }
    },
  });

  KbModel.contexts = Backbone.Collection.extend({
    model: KbModel.context,
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "status");
    },
    addItem: function () {
      const newContext = new KbModel.context();
      this.add(newContext);
      return newContext;
    },
    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/contexts";
    },
  });

  // NOTE: There is some overlap between this regular expression and the one
  // used to match ID values. But, TouchPoint names must support spaces, so
  // this needs to be a separate check.
  const TOUCHPOINT_NAME_RE = /^(\w|\s|-)*$/;

  KbModel.touchpoint = Backbone.Model.extend({
    idAttribute: "touchPointId",
    defaultValidateAttributes: ["touchPoint"],

    _validateDuplicateTouchPoint: function (value) {
      //touchpoint must be unique in the group
      for (let i = 0; i < this.collection.length; i++) {
        const touchPoint = this.collection.at(i);
        if (this !== touchPoint) {
          //don't check the entry against itself
          if (value.toUpperCase() === touchPoint.get("touchPoint").toUpperCase()) {
            return $.t("admin.kbAdmin.duplicateTouchPointError");
          }
        }
      }
    },

    _validateName: function (value) {
      if (!TOUCHPOINT_NAME_RE.test(value)) {
        return $.t("touchpoints.invalidTouchPointName");
      }
    },

    validateAttribute: function (attribute, value) {
      if (attribute == "touchPoint") {
        return (
          this.validateRequiredValue(value, { maxLength: 50 }) ||
          this._validateName(value) ||
          this._validateDuplicateTouchPoint(value)
        );
      }
    },
  });

  KbModel.touchpoints = Backbone.Collection.extend({
    model: KbModel.touchpoint,

    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "touchPoint");
    },

    addItem: function (touchPointName) {
      const newTouchPoint = new KbModel.touchpoint();
      newTouchPoint.set("touchPoint", touchPointName);
      this.add(newTouchPoint);
      return newTouchPoint;
    },

    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/touchpoints";
    },
  });

  KbModel.language = Backbone.Model.extend({
    idAttribute: "id",
  });

  KbModel.languages = Backbone.Collection.extend({
    model: KbModel.language,

    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "name");
    },

    addItem: function (languageId, name) {
      const newLanguage = new KbModel.language({
        id: languageId,
        name: name,
      });
      this.add(newLanguage);
      return newLanguage;
    },

    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/languages";
    },
  });

  KbModel.allLanguages = Backbone.Collection.extend({
    model: KbModel.language,
    initializeCustom: function () {
      this.on("sync", function () {
        this.sort();
      });
      this.fetch();
    },
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "name");
    },
    url: function () {
      return Properties.rootURL + "/languages";
    },
  });

  KbModel.responseTypeCapacity = Backbone.Model.extend({
    defaults: {
      usage: 0,
      capacity: 0,
    },

    initializeCustom: function (options) {
      this.kbId = options.kbId;
      this.fetch({ kbId: this.kb });
    },

    url: function () {
      return Properties.rootURL + "/" + this.kbId + "/responseTypeCapacityLimits";
    },
  });

  Messenger.reqres.setHandler("admin:kbs", function () {
    const kbs = new KbModel.knowledgeBases();
    kbs.fetch();
    kbs.on("sync", function () {
      this.sort();
    });
    return kbs;
  });

  Messenger.reqres.setHandler("tenant:kbs", function (tenantId) {
    const kbs = new KbModel.knowledgeBases();
    kbs.url = Properties.adminRootURL + "/tenants/" + tenantId + "/kbs";
    kbs.fetch();
    kbs.on("sync", function () {
      this.sort();
    });
    return kbs;
  });

  Messenger.reqres.setHandler("fetch:kb:admin:contexts", function (kbId) {
    return new KbModel.contexts({ kbId: kbId });
  });

  Messenger.reqres.setHandler("fetch:kbModel:touchpoints", function (kbId) {
    return new KbModel.touchpoints({ kbId: kbId });
  });

  Messenger.reqres.setHandler("fetch:kbModel:languages", function (kbId) {
    return new KbModel.languages({ kbId: kbId });
  });

  Messenger.reqres.setHandler("fetch:kb:allLanguages", function () {
    return new KbModel.allLanguages();
  });

  Messenger.reqres.setHandler("fetch:kbModel:responseTypeCapacity", function (kbId) {
    return new KbModel.responseTypeCapacity({ kbId: kbId });
  });

  Messenger.reqres.setHandler("kb:list:headers", function () {
    const Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({
        headerName: $.t("admin.kbAdmin.kbName"),
        modelAttribute: "kbName",
        selectable: true,
        sortable: true,
        filterable: true,
        longTextField: true,
      }),
      new Header({
        headerName: $.t("admin.kbAdmin.kbDescription"),
        modelAttribute: "kbDescription",
        longTextField: true,
        sortable: true,
        hideable: true,
        filterable: true,
      }),
      new Header({
        headerName: $.t("admin.kbAdmin.created"),
        modelAttribute: "creationDate",
        sortable: true,
        filterable: false,
        dateField: true,
        dateOnly: true,
      }),
      new Header({
        headerName: $.t("admin.kbAdmin.tenant"),
        modelAttribute: "_thisObject.tenantName",
        sortable: true,
        textField: true,
        filterable: true,
      }),
    ]);
  });

  Messenger.reqres.setHandler("kb:context:list:headers", function () {
    const Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({
        headerName: $.t("admin.kbAdmin.contexts"),
        modelAttribute: "status",
        selectable: false,
        sortable: false,
        filterable: false,
        longTextField: true,
      }),
      new Header({
        headerName: "",
        modelAttribute: "id",
        width: "15px",
        customHtml:
          '<button type="button" class="btn btn-danger btn-mini deleteKbContext pull-right" data-i18n="[title]admin.kbAdmin.deleteContext"> <i class="icon-remove"></i></button>',
      }),
    ]);
  });

  Messenger.reqres.setHandler("kb:locale:list:headers", function () {
    const Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({
        headerName: $.t("admin.kbAdmin.locales"),
        modelAttribute: "name",
        selectable: false,
        sortable: false,
        filterable: false,
        longTextField: true,
      }),
      new Header({
        headerName: "",
        modelAttribute: "tenantName",
        width: "15px",
        customHtml:
          '<button type="button" class="btn btn-danger btn-mini deleteKbLocale pull-right" data-i18n="[title]admin.tenantAdmin.deleteTenant"> <i class="icon-remove"></i></button>',
      }),
    ]);
  });

  Messenger.reqres.setHandler("kb:touchPoints:list:headers", function () {
    const Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({
        headerName: $.t("admin.kbAdmin.touchPoints"),
        modelAttribute: "touchPoint",
        selectable: false,
        sortable: false,
        filterable: false,
        longTextField: true,
      }),
      new Header({
        headerName: "",
        modelAttribute: "touchPoint",
        width: "15px",
        customHtml:
          '<button type="button" class="btn btn-danger btn-mini deleteKbTouchPoint pull-right" data-i18n="[title]admin.tenantAdmin.deleteTenant"> <i class="icon-remove"></i></button>',
      }),
    ]);
  });

  Messenger.reqres.setHandler("kb:engine:modes", function () {
    return ENGINE_MODE;
  });

  Messenger.reqres.setHandler("kb:engine:mode:label", function (engineModeValue) {
    switch (engineModeValue) {
      case ENGINE_MODE.NLP:
        return $.t("admin.kbAdmin.engineModeValue.NLP");
      case ENGINE_MODE.KEYWORD:
        return $.t("admin.kbAdmin.engineModeValue.KEYWORD");
      case ENGINE_MODE.SEMANTIC:
        return $.t("admin.kbAdmin.engineModeValue.SEMANTIC");
    }
  });

  return KbModel;
});
